import React, { useEffect, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/ui/Button';
import { AlertTriangleIcon, HelpCircleIcon, InfoIcon, XIcon, CheckCircle2Icon } from 'lucide-react';
interface ConfirmOptions {
  title: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'primary' | 'success';
  onConfirm: () => void | Promise<void>;
}
interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => void;
}
const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);
export function ConfirmProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [visible, setVisible] = useState(false);
  // Evita que un doble clic (o clic mientras la petición async está en curso)
  // dispare onConfirm más de una vez, lo que generaba registros duplicados
  // (ej. citas duplicadas) al crear datos contra Supabase.
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      setTimeout(() => {
        document.body.style.overflow = 'unset';
      }, 300);
    }
  }, [isOpen]);
  const confirm = (opts: ConfirmOptions) => {
    setOptions(opts);
    setIsProcessing(false);
    setIsOpen(true);
  };
  const handleConfirm = async () => {
    if (isProcessing) return; // ya se está procesando, ignorar clics adicionales
    if (options) {
      try {
        setIsProcessing(true);
        await options.onConfirm();
      } finally {
        setIsProcessing(false);
      }
    }
    setIsOpen(false);
  };
  const handleCancel = () => {
    if (isProcessing) return;
    setIsOpen(false);
  };
  const getIcon = () => {
    switch (options?.variant) {
      case 'danger':
        return <AlertTriangleIcon className="w-8 h-8 text-rose-600 dark:text-rose-400" data-id="element-6545" data-mp="ntoanm45gmw3" />;
      case 'warning':
        return <AlertTriangleIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" data-id="element-6546" data-mp="sm7qkpqb7u42" />;
      case 'info':
        return <InfoIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" data-id="element-6547" data-mp="wm047guk8qga" />;
      case 'success':
        return <CheckCircle2Icon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" data-id="element-6548" data-mp="3w3pyex63114h" />;
      default:
        return <HelpCircleIcon className="w-8 h-8 text-teal-600 dark:text-teal-400" data-id="element-6549" data-mp="8wxdzklv1pa6" />;
    }
  };
  const getIconBg = () => {
    switch (options?.variant) {
      case 'danger':
        return 'bg-rose-100 dark:bg-rose-900/30 ring-rose-50 dark:ring-rose-900/20';
      case 'warning':
        return 'bg-amber-100 dark:bg-amber-900/30 ring-amber-50 dark:ring-amber-900/20';
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900/30 ring-blue-50 dark:ring-blue-900/20';
      case 'success':
        return 'bg-emerald-100 dark:bg-emerald-900/30 ring-emerald-50 dark:ring-emerald-900/20';
      default:
        return 'bg-teal-100 dark:bg-teal-900/30 ring-teal-50 dark:ring-teal-900/20';
    }
  };
  const getButtonVariant = () => {
    switch (options?.variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'primary';
      // We use primary but could style it amber if we had a warning variant
      default:
        return 'primary';
    }
  };
  const modalContent = isOpen ? <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-0" data-id="element-6550" data-mp="16px0kp2qrb3y">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300" style={{
      opacity: visible ? 1 : 0
    }} onClick={handleCancel} data-id="element-6551" data-mp="12pwlr3bqcxwd" />
    

      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transition-all duration-300 flex flex-col" style={{
      transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(10px)',
      opacity: visible ? 1 : 0
    }} data-id="element-6552" data-mp="2x0rrzyzg00vz">
      
        <button onClick={handleCancel} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" data-id="element-6553" data-mp="2z6pt0pda2h45">
        
          <XIcon className="w-5 h-5" data-id="element-6554" data-mp="2q731cvacopoa" />
        </button>

        <div className="p-6 pt-8 flex flex-col items-center text-center" data-id="element-6555" data-mp="2bhevrhv4z4ur">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ring-8 mb-5 ${getIconBg()}`} data-id="element-6556" data-mp="2bfc0aib7sjin">
          
            {getIcon()}
          </div>

          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-6557" data-mp="22h8pafxuv553">
            {options?.title || 'Confirmar acción'}
          </h3>

          <div className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-8" data-id="element-6558" data-mp="1nu04qoinpngg">
            {options?.message}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-6559" data-mp="1ivymu6wyvu6k">
            <Button variant="outline" onClick={handleCancel} disabled={isProcessing} className="w-full sm:flex-1 justify-center py-2.5" data-id="element-6560" data-mp="1wugc492iteql">
            
              {options?.cancelText || 'Cancelar'}
            </Button>
            <Button variant={getButtonVariant()} onClick={handleConfirm} disabled={isProcessing} className="w-full sm:flex-1 justify-center py-2.5" data-id="element-6561" data-mp="26qt0crfar0dy">
            
              {isProcessing ? 'Procesando...' : options?.confirmText || 'Confirmar'}
            </Button>
          </div>
        </div>
      </div>
    </div> : null;
  return <ConfirmContext.Provider value={{
    confirm
  }} data-id="element-6562" data-mp="2pjn7x0albq85">
      
      {children}
      {typeof document !== 'undefined' && createPortal(modalContent, document.body)}
    </ConfirmContext.Provider>;
}
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}